import { useState, useEffect } from "react";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { msalInstance, loginRequest, msalConfig } from "./msalConfig";
import { BrowserAuthError } from "@azure/msal-browser";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Divider,
  Paper,
  Alert
} from "@mui/material";
import { Icon } from "@iconify/react";

// Wrapper component that contains the auth modal logic
function AuthWrapper({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const [isDialogOpen, setIsDialogOpen] = useState(true); // Start with dialog open by default
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const isAuthenticated = accounts.length > 0;

  // Initialize and handle redirect on mount
  useEffect(() => {
    const checkInitialization = async () => {
      if (instance) {
        try {
          // Ensure the instance is initialized before using it
          await instance.initialize();
          setIsInitialized(true);
          
          // Handle any redirects
          try {
            const response = await instance.handleRedirectPromise();
            if (response) {
              console.log("Redirect authentication successful:", response);
              
              // Restore any saved URL state
              const savedUrl = sessionStorage.getItem("loginRedirectUrl");
              if (savedUrl && savedUrl !== window.location.href) {
                console.log("Restoring saved URL:", savedUrl);
                sessionStorage.removeItem("loginRedirectUrl");
                // Only redirect if it's different from current URL
                if (savedUrl !== window.location.href) {
                  window.location.href = savedUrl;
                  return;
                }
              }
            }
          } catch (error) {
            console.error("Error handling redirect:", error);
            // Clear any potentially corrupted redirect state
            sessionStorage.removeItem("loginRedirectUrl");
          }
        } catch (error) {
          console.error("Failed to initialize MSAL instance:", error);
          setLoginError("Authentication system initialization failed. Please refresh the page or contact support.");
        }
      }
    };
    
    checkInitialization();
  }, [instance]);

  // Force the dialog to close only after authentication is successful
  useEffect(() => {
    if (isAuthenticated) {
      setIsDialogOpen(false);
      setIsLoggingIn(false);
      setLoginError(null);
    }
  }, [isAuthenticated]);

  const handleLogin = async () => {
    if (!isInitialized) {
      setLoginError("Authentication system is initializing. Please try again in a moment.");
      return;
    }
    
    setIsLoggingIn(true);
    setLoginError(null);
    
    try {
      const redirectUri = msalConfig.auth.redirectUri;
      console.log("Starting popup login with redirect URI:", redirectUri);
      
      await instance.loginPopup({
        ...loginRequest,
        redirectUri: redirectUri,
        popupWindowAttributes: {
          popupSize: { width: 600, height: 600 },
          popupPosition: { top: 100, left: 100 }
        }
      });
    } catch (error) {
      console.error("Sign in failed:", error);
      
      // If we get a hash_empty_error or any popup related error, try redirect method as fallback
      if (error instanceof BrowserAuthError) {
        try {
          console.log("Attempting redirect login as fallback...");
          // Store the current URL to return to after authentication
          sessionStorage.setItem("loginRedirectUrl", window.location.href);
          
          // Use the same redirect URI as configured in msalConfig
          const redirectUri = msalConfig.auth.redirectUri;
          console.log("Using redirect URI:", redirectUri);
          
          await instance.loginRedirect({
            ...loginRequest,
            redirectUri: redirectUri
          });
          return; // The page will reload after redirect
        } catch (redirectError) {
          console.error("Redirect login also failed:", redirectError);
          setLoginError("Authentication failed. Please try again or contact support.");
        }
      } else {
        setLoginError(error.message || "Authentication failed. Please try again.");
      }
      
      setIsLoggingIn(false);
    }
  };

  return (
    <>
      {/* Main application content */}
      {children}

      {/* Non-closable authentication dialog */}
      <Dialog
        open={isDialogOpen}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        onClose={(event, reason) => {
          // Prevent dialog from closing when clicking outside
          if (reason === 'backdropClick') {
            return false;
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 5,
            maxWidth: { xs: '90vw', sm: '600px', md: '800px' },
            maxHeight: '90vh',
            width: { xs: '90vw', sm: '600px', md: '800px' }
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: { xs: 'auto', md: '400px' }
        }}>
          {/* Left Section - Parts360 Branding */}
          <Box sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 2, md: 3 },
            position: 'relative',
            overflow: 'hidden',
            minHeight: { xs: '200px', md: 'auto' }
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              background: 'url(/ethos.svg) no-repeat center center',
              backgroundSize: '150%',
              filter: 'blur(3px)'
            }} />

            <Box sx={{ position: 'relative', mb: { xs: 2, md: 3 } }}>
              <img src="/ethos.svg" alt="Parts360 Logo" width={60} height={60} />
            </Box>

            <Typography variant="h4" fontWeight="bold" fontFamily="var(--inter-font)" sx={{ 
              mb: { xs: 2, md: 3 }, 
              position: 'relative',
              fontSize: { xs: '1.5rem', md: '2.125rem' }
            }}>
              Parts360
            </Typography>

            <Typography variant="body1" sx={{
              textAlign: 'center',
              maxWidth: '300px',
              position: 'relative',
              fontWeight: '400',
              fontFamily: "var(--inter-font)",
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}>
              Access intelligent insights and get answers to all your parts and inventory questions.
            </Typography>
          </Box>

          {/* Right Section - Sign In */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              flex: 1
            }}
          >
            <Typography variant="h5" fontWeight="bold" fontFamily="var(--inter-font)" sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '1.25rem', md: '1.5rem' }
            }}>
              Authentication Required
            </Typography>

            <Typography variant="body1" sx={{ 
              mb: { xs: 3, md: 4 }, 
              fontFamily: "var(--inter-font)",
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}>
              Please sign in with your Microsoft account to access the Parts360 AI Agent.
            </Typography>

            {loginError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {loginError}
              </Alert>
            )}

            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                mb: { xs: 3, md: 4 }
              }}
            >
              <Typography variant="body2" sx={{ 
                mb: 2, 
                fontFamily: "var(--inter-font)",
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}>
                By signing in, you'll be able to:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                <Typography component="li" variant="body2" sx={{ 
                  mb: 1, 
                  fontFamily: "var(--inter-font)",
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  Access Parts360 data and generate insights
                </Typography>
                <Typography component="li" variant="body2" sx={{ 
                  mb: 1, 
                  fontFamily: "var(--inter-font)",
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  Save and retrieve your conversation history
                </Typography>
                <Typography component="li" variant="body2" sx={{ 
                  fontFamily: "var(--inter-font)",
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  Use the AI Agent with your organizational permissions
                </Typography>
              </Box>
            </Paper>

            <Button
              variant="contained"
              size="large"
              disabled={isLoggingIn || !isInitialized}
              onClick={handleLogin}
              startIcon={<Icon icon="logos:microsoft-icon" width={20} height={20} />}
              sx={{
                py: { xs: 1, md: 1.5 },
                px: { xs: 2, md: 3 },
                borderRadius: 2,
                textTransform: 'none',
                fontSize: { xs: '0.875rem', md: '1rem' },
                width: { xs: '100%', md: 'auto' }
              }}
            >
              {isLoggingIn ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Signing in...
                </>
              ) : !isInitialized ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Initializing...
                </>
              ) : (
                <Typography variant="button" fontFamily="var(--inter-font)" sx={{ 
                  fontWeight: '500', 
                  textTransform:'none',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}>
                  Sign in with Microsoft
                </Typography>
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

export function AuthProvider({ children }) {
  const [msalReady, setMsalReady] = useState(false);
  
  // Make sure MSAL is initialized before rendering the MsalProvider
  useEffect(() => {
    const initializeMsal = async () => {
      if (msalInstance) {
        try {
          // Ensure MSAL is initialized
          await msalInstance.initialize();
          setMsalReady(true);
          
          // Handle any lingering redirects from authentication
          msalInstance.handleRedirectPromise()
            .then(response => {
              if (response) {
                console.log("Redirect authentication successful");
                // Restore any saved URL state
                const redirectUrl = sessionStorage.getItem("loginRedirectUrl");
                if (redirectUrl) {
                  sessionStorage.removeItem("loginRedirectUrl");
                  window.location.href = redirectUrl;
                }
              }
            })
            .catch(error => {
              console.error("Redirect authentication error:", error);
            });
        } catch (error) {
          console.error("Failed to initialize MSAL:", error);
        }
      }
    };
    
    initializeMsal();
  }, []);
  
  // Don't render anything until MSAL is initialized
  if (!msalReady) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Initializing authentication...
        </Typography>
      </Box>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthWrapper>
        {children}
      </AuthWrapper>
    </MsalProvider>
  );
}